import React, {useEffect, useMemo, useState} from "react";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {MultiSelect} from "primereact/multiselect";
import {Dialog} from "primereact/dialog";
import {CONTENT_TYPE_UIDS} from "../../system/content-type-registry";
import {useContentTypeData} from "../../system/use-content-type-data";
import {useTranslate} from "../../system/useTranslate";
import {entityId, entityLabel} from "../operations/operations-utils";
import {getErrorMessage} from "../sales/sales-utils";

const CAPABILITIES = [
    "text",
    "stream",
    "structured_output",
    "tool_calling",
    "embedding",
    "vision",
    "audio",
];

function relationValue(value) {
    return entityId(value) || null;
}

function FormField({label, children, hint}) {
    const translate = useTranslate();
    const translatedLabel = translate(label);
    const resolvedHint = hint
        ? translate(hint)
        : `${translate("form-field-purpose-prefix")}${translatedLabel}${translate("form-field-purpose-suffix")}`;
    return (
        <div className="col-12 md:col-6 flex flex-column gap-2">
            <label className="text-xs font-bold text-700">{translatedLabel}</label>
            {children}
            <small className="text-xs text-500 line-height-3">{resolvedHint}</small>
        </div>
    );
}

export function AiEngineEditor({visible, entity, data, onHide}) {
    const translate = useTranslate();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!visible) return;
        setForm({
            engineKey: entity?.engineKey || "",
            displayName: entity?.displayName || "",
            engineStatus: entity?.engineStatus || "active",
            description: entity?.description || "",
            requiredCapabilities: Array.isArray(entity?.requiredCapabilities)
                ? entity.requiredCapabilities
                : [],
        });
        setError(null);
    }, [entity, visible]);

    const setField = (field, value) => setForm((current) => ({...current, [field]: value}));
    const save = async () => {
        if (!form.engineKey.trim() || !form.displayName.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const payload = {
                engineKey: form.engineKey.trim(),
                displayName: form.displayName.trim(),
                engineStatus: form.engineStatus,
                description: form.description.trim() || null,
                requiredCapabilities: form.requiredCapabilities,
            };
            if (entity) await data.update(entity.documentId || entity.id, payload);
            else await data.create(payload);
            onHide();
        } catch (requestError) {
            setError(getErrorMessage(requestError, translate("ai-admin-save-error")));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            visible={visible}
            onHide={() => !saving && onHide()}
            header={translate(entity ? "ai-admin-edit-engine" : "ai-admin-create-engine")}
            className="rtl w-11 md:w-8 lg:w-6"
            modal
            draggable={false}
        >
            <div className="flex flex-column gap-3">
                {error && <Message severity="error" text={translate(error)} className="text-xs"/>}
                <Message severity="info" text={translate("ai-admin-engine-form-description")} className="text-xs"/>
                <div className="grid">
                    <FormField label="ai-admin-engine-key" hint="ai-admin-field-engine-key-hint">
                        <InputText
                            className="w-full ltr"
                            value={form.engineKey || ""}
                            disabled={Boolean(entity)}
                            onChange={(event) => setField("engineKey", event.target.value)}
                        />
                    </FormField>
                    <FormField label="ai-admin-engine-display-name" hint="ai-admin-field-display-name-hint">
                        <InputText
                            className="w-full"
                            value={form.displayName || ""}
                            onChange={(event) => setField("displayName", event.target.value)}
                        />
                    </FormField>
                    <FormField label="status" hint="ai-admin-field-engine-status-hint">
                        <Dropdown
                            className="w-full"
                            value={form.engineStatus}
                            options={["active", "disabled"].map((value) => ({
                                value,
                                label: translate(`ai-admin-status-${value}`),
                            }))}
                            onChange={(event) => setField("engineStatus", event.value)}
                        />
                    </FormField>
                    <FormField label="ai-admin-required-capabilities" hint="ai-admin-field-required-capabilities-hint">
                        <MultiSelect
                            className="w-full"
                            value={form.requiredCapabilities || []}
                            options={CAPABILITIES.map((value) => ({
                                value,
                                label: translate(`ai-admin-capability-${value.replace(/_/g, "-")}`),
                            }))}
                            display="chip"
                            onChange={(event) => setField("requiredCapabilities", event.value)}
                        />
                    </FormField>
                    <div className="col-12 flex flex-column gap-2">
                        <label className="text-xs font-bold text-700">{translate("description")}</label>
                        <InputTextarea
                            className="w-full"
                            rows={4}
                            value={form.description || ""}
                            onChange={(event) => setField("description", event.target.value)}
                        />
                        <small className="text-xs text-500 line-height-3">{translate("ai-admin-field-description-hint")}</small>
                    </div>
                </div>
                <div className="flex justify-content-end gap-2">
                    <Button icon="pi pi-times" label={translate("cancel")} className="gap-2" text onClick={onHide}/>
                    <Button
                        icon="pi pi-save"
                        label={translate("save-changes")}
                        className="gap-2"
                        loading={saving}
                        disabled={!form.engineKey?.trim() || !form.displayName?.trim()}
                        onClick={save}
                    />
                </div>
            </div>
        </Dialog>
    );
}

export function AiEngineConfigEditor({visible, entity, data, onHide}) {
    const translate = useTranslate();
    const engines = useContentTypeData(CONTENT_TYPE_UIDS.AI_ENGINE);
    const models = useContentTypeData(CONTENT_TYPE_UIDS.AI_MODEL);
    const prompts = useContentTypeData(CONTENT_TYPE_UIDS.AI_PROMPT);
    const workspaces = useContentTypeData(CONTENT_TYPE_UIDS.WORKSPACE);
    const ensureEngines = engines.ensure;
    const ensureModels = models.ensure;
    const ensurePrompts = prompts.ensure;
    const ensureWorkspaces = workspaces.ensure;
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!visible) return;
        Promise.allSettled([
            ensureEngines({}, {all: true}),
            ensureModels({}, {all: true}),
            ensurePrompts({}, {all: true}),
            ensureWorkspaces({}, {all: true}),
        ]);
    }, [ensureEngines, ensureModels, ensurePrompts, ensureWorkspaces, visible]);

    useEffect(() => {
        if (!visible) return;
        const retryPolicy = entity?.retryPolicy || {};
        const parameters = entity?.parameters || {};
        const cachePolicy = entity?.cachePolicy || {};
        setForm({
            engine: relationValue(entity?.engine),
            workspace: relationValue(entity?.workspace),
            primaryModel: relationValue(entity?.primaryModel),
            fallbackModel: relationValue(entity?.fallbackModel),
            prompt: relationValue(entity?.prompt),
            configStatus: entity?.configStatus || "active",
            maxOutputTokens: entity?.maxOutputTokens ?? 2000,
            timeoutMs: entity?.timeoutMs ?? 60000,
            perRequestCreditCap: entity?.perRequestCreditCap ?? null,
            dailyCreditCap: entity?.dailyCreditCap ?? null,
            monthlyCreditCap: entity?.monthlyCreditCap ?? null,
            minimumCredit: entity?.minimumCredit ?? 0,
            allowFallback: entity?.allowFallback !== false,
            temperature: parameters.temperature ?? 0.2,
            topP: parameters.topP ?? 1,
            maxRetries: retryPolicy.maxRetries ?? 1,
            retryableCodes: Array.isArray(retryPolicy.retryableCodes)
                ? retryPolicy.retryableCodes.join(", ")
                : "",
            cacheEnabled: cachePolicy.enabled === true,
            cacheTtlSeconds: cachePolicy.ttlSeconds ?? 0,
            originalParameters: parameters,
            originalRetryPolicy: retryPolicy,
            originalCachePolicy: cachePolicy,
        });
        setError(null);
    }, [entity, visible]);

    const setField = (field, value) => setForm((current) => ({...current, [field]: value}));
    const options = (items) => items.map((item) => ({
        value: entityId(item),
        label: entityLabel(item),
    }));
    const engineOptions = useMemo(() => options(engines.items), [engines.items]);
    const modelOptions = useMemo(() => options(models.items), [models.items]);
    const promptOptions = useMemo(() => options(prompts.items), [prompts.items]);
    const workspaceOptions = useMemo(() => [
        {value: null, label: translate("ai-admin-global-default")},
        ...options(workspaces.items),
    ], [translate, workspaces.items]);

    const save = async () => {
        if (!form.engine || !form.primaryModel) return;
        setSaving(true);
        setError(null);
        try {
            const payload = {
                engine: form.engine,
                workspace: form.workspace || null,
                primaryModel: form.primaryModel,
                fallbackModel: form.fallbackModel || null,
                prompt: form.prompt || null,
                configStatus: form.configStatus,
                maxOutputTokens: form.maxOutputTokens,
                timeoutMs: form.timeoutMs,
                perRequestCreditCap: form.perRequestCreditCap,
                dailyCreditCap: form.dailyCreditCap,
                monthlyCreditCap: form.monthlyCreditCap,
                minimumCredit: form.minimumCredit,
                allowFallback: form.allowFallback,
                parameters: {
                    ...form.originalParameters,
                    temperature: form.temperature,
                    topP: form.topP,
                },
                retryPolicy: {
                    ...form.originalRetryPolicy,
                    maxRetries: form.maxRetries,
                    retryableCodes: String(form.retryableCodes || "")
                        .split(",")
                        .map((value) => value.trim())
                        .filter(Boolean),
                },
                cachePolicy: {
                    ...form.originalCachePolicy,
                    enabled: form.cacheEnabled,
                    ttlSeconds: form.cacheTtlSeconds,
                },
            };
            if (entity) await data.update(entity.documentId || entity.id, payload);
            else await data.create(payload);
            onHide();
        } catch (requestError) {
            setError(getErrorMessage(requestError, translate("ai-admin-save-error")));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            visible={visible}
            onHide={() => !saving && onHide()}
            header={translate(entity ? "ai-admin-edit-engine-config" : "ai-admin-create-engine-config")}
            className="rtl w-11 md:w-10 lg:w-8"
            modal
            draggable={false}
        >
            <div className="flex flex-column gap-3">
                {error && <Message severity="error" text={translate(error)} className="text-xs"/>}
                <Message severity="info" text={translate("ai-admin-config-form-description")} className="text-xs"/>
                <div className="grid">
                    <FormField label="ai-admin-engine" hint="ai-admin-field-engine-hint">
                        <Dropdown className="w-full" filter value={form.engine} options={engineOptions} onChange={(event) => setField("engine", event.value)}/>
                    </FormField>
                    <FormField label="workspace" hint="ai-admin-field-workspace-hint">
                        <Dropdown className="w-full" filter value={form.workspace} options={workspaceOptions} onChange={(event) => setField("workspace", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-primary-model" hint="ai-admin-field-primary-model-hint">
                        <Dropdown className="w-full" filter value={form.primaryModel} options={modelOptions} onChange={(event) => setField("primaryModel", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-fallback-model" hint="ai-admin-field-fallback-model-hint">
                        <Dropdown className="w-full" filter showClear value={form.fallbackModel} options={modelOptions} onChange={(event) => setField("fallbackModel", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-prompt" hint="ai-admin-field-prompt-hint">
                        <Dropdown className="w-full" filter showClear value={form.prompt} options={promptOptions} onChange={(event) => setField("prompt", event.value)}/>
                    </FormField>
                    <FormField label="status" hint="ai-admin-field-config-status-hint">
                        <Dropdown
                            className="w-full"
                            value={form.configStatus}
                            options={["active", "disabled"].map((value) => ({
                                value,
                                label: translate(`ai-admin-status-${value}`),
                            }))}
                            onChange={(event) => setField("configStatus", event.value)}
                        />
                    </FormField>
                    <FormField label="ai-admin-temperature" hint="ai-admin-temperature-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={form.temperature} min={0} max={2} minFractionDigits={0} maxFractionDigits={2} onValueChange={(event) => setField("temperature", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-top-p" hint="ai-admin-field-top-p-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={form.topP} min={0} max={1} minFractionDigits={0} maxFractionDigits={2} onValueChange={(event) => setField("topP", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-max-output-tokens" hint="ai-admin-field-max-output-tokens-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={form.maxOutputTokens} min={1} onValueChange={(event) => setField("maxOutputTokens", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-timeout-ms" hint="ai-admin-field-timeout-ms-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={form.timeoutMs} min={1000} onValueChange={(event) => setField("timeoutMs", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-max-retries" hint="ai-admin-field-max-retries-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={form.maxRetries} min={0} max={10} onValueChange={(event) => setField("maxRetries", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-retryable-codes" hint="ai-admin-retryable-codes-hint">
                        <InputText className="w-full ltr" value={form.retryableCodes || ""} onChange={(event) => setField("retryableCodes", event.target.value)}/>
                    </FormField>
                    <FormField label="ai-admin-per-request-credit-cap" hint="ai-admin-field-per-request-credit-cap-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={Number(form.perRequestCreditCap || 0)} min={0} onValueChange={(event) => setField("perRequestCreditCap", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-daily-credit-cap" hint="ai-admin-field-daily-credit-cap-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={Number(form.dailyCreditCap || 0)} min={0} onValueChange={(event) => setField("dailyCreditCap", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-monthly-credit-cap" hint="ai-admin-field-monthly-credit-cap-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={Number(form.monthlyCreditCap || 0)} min={0} onValueChange={(event) => setField("monthlyCreditCap", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-minimum-credit" hint="ai-admin-field-minimum-credit-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={Number(form.minimumCredit || 0)} min={0} onValueChange={(event) => setField("minimumCredit", event.value)}/>
                    </FormField>
                    <FormField label="ai-admin-cache-ttl-seconds" hint="ai-admin-field-cache-ttl-seconds-hint">
                        <InputNumber className="w-full" inputClassName="w-full ltr" value={form.cacheTtlSeconds} min={0} onValueChange={(event) => setField("cacheTtlSeconds", event.value)}/>
                    </FormField>
                    <div className="col-12 md:col-6 flex flex-column gap-2">
                        <label className="p-3 border-1 border-200 border-round flex align-items-center justify-content-between gap-3">
                            <span className="text-xs">{translate("ai-admin-allow-fallback")}</span>
                            <Checkbox checked={Boolean(form.allowFallback)} onChange={(event) => setField("allowFallback", event.checked)}/>
                        </label>
                        <small className="text-xs text-500 line-height-3">{translate("ai-admin-field-allow-fallback-hint")}</small>
                    </div>
                    <div className="col-12 md:col-6 flex flex-column gap-2">
                        <label className="p-3 border-1 border-200 border-round flex align-items-center justify-content-between gap-3">
                            <span className="text-xs">{translate("ai-admin-cache-enabled")}</span>
                            <Checkbox checked={Boolean(form.cacheEnabled)} onChange={(event) => setField("cacheEnabled", event.checked)}/>
                        </label>
                        <small className="text-xs text-500 line-height-3">{translate("ai-admin-field-cache-enabled-hint")}</small>
                    </div>
                </div>
                <div className="flex justify-content-end gap-2">
                    <Button icon="pi pi-times" label={translate("cancel")} className="gap-2" text onClick={onHide}/>
                    <Button
                        icon="pi pi-save"
                        label={translate("save-changes")}
                        className="gap-2"
                        loading={saving}
                        disabled={!form.engine || !form.primaryModel}
                        onClick={save}
                    />
                </div>
            </div>
        </Dialog>
    );
}
